import { API_CONFIG, getHeaders, PaginatedResponse } from '../config/settings'

export interface ServiceItem {
  id: number | string
  title: string
  slug: string
  summary: string
  icon?: string | null
  is_featured?: boolean
}

export interface PackageItem {
  id: number
  title: string
  slug: string
  description: string
  featured_img?: string | null
  price: number
  max_persons?: number
  number_of_days?: number
  avg_rating: number
  vendor_username?: string
}

export interface VendorItem {
  id: number
  username: string
  name: string
  photo?: string | null
  city: string
  state: string
  address: string
  avg_rating: number
  room_count: number
  package_count: number
}

export interface BlogItem {
  id: string
  title: string
  slug: string
  summary?: string
  image?: string | null
  published_at?: string
  source?: string
  reading_minutes?: number
}

export interface YouTubeVideo {
  video_id: string
  title: string
  url: string
  channel: string
  category: string
}

export interface BlogsResponse extends PaginatedResponse<BlogItem> {
  youtube_videos: YouTubeVideo[]
  youtube_count: number
}

export interface GalleryResponse {
  status: string
  headline: string
  message: string
  preview_images: { src: string; alt: string }[]
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_CONFIG.BASE_URL}${path}`, { headers: getHeaders() })
  if (!res.ok) throw new Error(`Request failed: ${res.status}`)
  return res.json()
}

export const contentApi = {
  getServices: (page = 1) => fetchJson<PaginatedResponse<ServiceItem>>(`/services?page=${page}`),
  getService: (id: string) => fetchJson<ServiceItem & { content?: string }>(`/services/${encodeURIComponent(id)}`),
  getPackages: (page = 1, search?: string) => {
    const q = new URLSearchParams({ page: String(page) })
    if (search) q.set('search', search)
    return fetchJson<PaginatedResponse<PackageItem>>(`/packages?${q}`)
  },
  getPackage: (id: number) => fetchJson<PackageItem>(`/packages/${id}`),
  getVendors: (page = 1, search?: string, location?: string) => {
    const q = new URLSearchParams({ page: String(page) })
    if (search) q.set('search', search)
    if (location) q.set('location', location)
    return fetchJson<PaginatedResponse<VendorItem>>(`/vendors?${q}`)
  },
  getVendor: (username: string) => fetchJson<VendorItem & { rooms: { id: number; title: string; rent: number; featured_img?: string }[] }>(`/vendors/${encodeURIComponent(username)}`),
  getBlogs: (page = 1, search?: string) => {
    const q = new URLSearchParams({ page: String(page) })
    if (search) q.set('search', search)
    return fetchJson<BlogsResponse>(`/blogs?${q}`)
  },
  getBlog: (slug: string) => fetchJson<BlogItem & { content?: string; youtube_videos?: YouTubeVideo[] }>(`/blogs/${encodeURIComponent(slug)}`),
  getGallery: () => fetchJson<GalleryResponse>('/gallery'),
}
